import React, { useState, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Stack,
} from "@mui/material";
import { UserQuestion } from "components/hooks/useTaskAgentState";

interface QuestionAnswerInputProps {
  question: UserQuestion;
  onAnswer: (answer: string) => void;
  onSkip: () => void;
  showNavigation?: boolean;
  currentAnswer?: string; // Pre-populated answer from previous selections
}

export interface QuestionAnswerInputRef {
  getCurrentAnswer: () => string;
}

const QuestionAnswerInput = forwardRef<QuestionAnswerInputRef, QuestionAnswerInputProps>(({
  question,
  onAnswer,
  onSkip,
  showNavigation = true,
  currentAnswer,
}, ref) => {
  const [stringValue, setStringValue] = useState("");
  const [numberValue, setNumberValue] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [datetimeValue, setDatetimeValue] = useState("");
  const [rangeValue, setRangeValue] = useState("");
  const [singleSelectValue, setSingleSelectValue] = useState("");
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);

  // Initialize values from currentAnswer or existing question answer
  React.useEffect(() => {
    const answerToUse = currentAnswer || question.answer || "";
    
    switch (question.question_type) {
      case "string":
        setStringValue(answerToUse);
        break;
      case "number":
        setNumberValue(answerToUse);
        break;
      case "date":
        setDateValue(answerToUse);
        break;
      case "datetime":
        setDatetimeValue(answerToUse);
        break;
      case "range":
        setRangeValue(answerToUse);
        break;
      case "single_select":
        setSingleSelectValue(answerToUse);
        break;
      case "multi_select":
        setMultiSelectValue(answerToUse ? answerToUse.split(", ") : []);
        break;
      default:
        setStringValue(answerToUse);
    }
  }, [question.id, currentAnswer, question.answer, question.question_type]);

  const getCurrentAnswer = () => {
    switch (question.question_type) {
      case "string":
        return stringValue;
      case "number":
        return numberValue;
      case "date":
        return dateValue;
      case "datetime":
        return datetimeValue;
      case "range":
        return rangeValue;
      case "single_select":
        return singleSelectValue;
      case "multi_select":
        return multiSelectValue.join(", ");
      default:
        return stringValue;
    }
  };

  useImperativeHandle(ref, () => ({
    getCurrentAnswer,
  }));

  const handleSubmit = () => {
    const answer = getCurrentAnswer();
    if (answer.trim()) {
      console.log(`Question answered: "${question.question}" -> "${answer}"`);
      onAnswer(answer);
    }
  };

  // Call onAnswer whenever the value changes to update the parent state
  React.useEffect(() => {
    const answer = getCurrentAnswer();
    // Always call onAnswer, even with empty answers, so parent can track state
    onAnswer(answer);
  }, [stringValue, numberValue, dateValue, datetimeValue, rangeValue, singleSelectValue, multiSelectValue]);

  const handleSkip = () => {
    console.log(`Question skipped: "${question.question}"`);
    onSkip();
  };

  const handleMultiSelectChange = (value: string, checked: boolean) => {
    if (checked) {
      setMultiSelectValue([...multiSelectValue, value]);
    } else {
      setMultiSelectValue(multiSelectValue.filter((v) => v !== value));
    }
  };

  const isValid = () => {
    switch (question.question_type) {
      case "string":
        return stringValue.trim() !== "";
      case "number":
        return numberValue.trim() !== "" && !isNaN(Number(numberValue));
      case "date":
        return dateValue !== "";
      case "datetime":
        return datetimeValue !== "";
      case "range":
        return rangeValue.trim() !== "";
      case "single_select":
        return singleSelectValue !== "";
      case "multi_select":
        return multiSelectValue.length > 0;
      default:
        return stringValue.trim() !== "";
    }
  };

  const renderInput = () => {
    switch (question.question_type) {
      case "string":
        return (
          <TextField
            label="Please provide your answer"
            fullWidth
            variant="outlined"
            value={stringValue}
            onChange={(e) => setStringValue(e.target.value)}
            placeholder="Enter your answer..."
            size="small"
          />
        );

      case "number":
        return (
          <TextField
            label="Please provide your answer"
            fullWidth
            variant="outlined"
            type="number"
            value={numberValue}
            onChange={(e) => setNumberValue(e.target.value)}
            placeholder="Enter a number..."
            size="small"
          />
        );

      case "date":
        return (
          <TextField
            label="Please provide your answer"
            fullWidth
            variant="outlined"
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case "datetime":
        return (
          <TextField
            label="Please provide your answer"
            fullWidth
            variant="outlined"
            type="datetime-local"
            value={datetimeValue}
            onChange={(e) => setDatetimeValue(e.target.value)}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case "range":
        return (
          <TextField
            label="Please provide your answer"
            fullWidth
            variant="outlined"
            value={rangeValue}
            onChange={(e) => setRangeValue(e.target.value)}
            placeholder="e.g., $1000-$5000, 25-35, etc."
            size="small"
          />
        );

      case "single_select":
        return (
          <FormControl component="fieldset">
            <RadioGroup
              value={singleSelectValue}
              onChange={(e) => setSingleSelectValue(e.target.value)}
            >
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size="small" />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case "multi_select":
        return (
          <FormControl component="fieldset">
            <FormGroup>
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={multiSelectValue.includes(option)}
                      onChange={(e) =>
                        handleMultiSelectChange(option, e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      default:
        return (
          <TextField
            fullWidth
            variant="outlined"
            value={stringValue}
            onChange={(e) => setStringValue(e.target.value)}
            placeholder="Enter your answer..."
            size="small"
          />
        );
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        border: "1px solid #e9ecef",
        borderRadius: "8px",
        padding: "16px",
        marginTop: "12px",
      }}
    >
      {renderInput()}

      {showNavigation && (
        <Stack direction="row" spacing={2} sx={{ marginTop: "12px" }}>
          <Button
            variant="text"
            onClick={handleSkip}
            size="small"
            sx={{ minWidth: "60px" }}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid()}
            size="small"
            sx={{ minWidth: "80px" }}
          >
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
});

export default QuestionAnswerInput;
